import type { CourseProgressHistoryReportData } from 'teacher-ui-client/components/reporting/writing-legends/course-progress/history/report-loader'
import Component from '@glimmer/component'
import { SegmentedBar } from 'teacher-ui-client/components/giraffe/primitives/segmented-bar'
import { range } from 'lodash'
import type { Point, AxisTickPositionsArray, PointOptionsObject, YAxisPlotLinesOptions } from 'highcharts'
import { t } from 'ember-intl'
import { colors } from '@colors'
import type { IntlService } from 'ember-intl'
import { service } from '@ember/service'
import type { AxisLabelsFormatterContextObject } from 'highcharts'
import { ChartTooltip } from 'teacher-ui-client/components/reporting/writing-legends/course-progress/chart-tooltip'

interface Signature {
  Element: HTMLDivElement
  Args: {
    studentsReportData: CourseProgressHistoryReportData['students']
  }
}

export class CourseProgressHistoryChart extends Component<Signature> {
  @service intl!: IntlService

  get customLegend(): { name: string; color: string }[] {
    return [
      {
        name: this.intl.t('lessons'),
        color: colors.oceanyBlue325,
      },
    ]
  }

  get seriesData(): {
    data: Array<PointOptionsObject>
    name: string
  }[] {
    const [minLessonNumber, maxLessonNumber] = this.lessonRange

    // must start on 0 (and adding min plus a buffer) because highcharts skips the 'min' number of value
    // from the start if you don't give it proper values for x, which we're not doing here
    return range(0, maxLessonNumber + minLessonNumber).map((lessonNumber) => {
      const seriesData = this.args.studentsReportData.map((student) => {
        const seriesDataItem = {
          value: 1,
          color: 'transparent',
          student,
          lesson: lessonNumber,
        }
        const completedLessonData = student.lessons?.find((lessonData) => lessonData.lesson === lessonNumber)
        return completedLessonData
          ? {
              ...seriesDataItem,
              color: colors.oceanyBlue325,
            }
          : seriesDataItem
      })
      return { name: String(lessonNumber), data: seriesData }
    })
  }

  get allLessonNumbers(): number[] {
    return this.args.studentsReportData.flatMap((student) => student.lessons.map(({ lesson }) => lesson))
  }

  // map numbers trailing a sequence of lesson numbers
  get plotLines(): YAxisPlotLinesOptions[] {
    return []
  }

  // one tick for each lesson number
  get tickPositions(): AxisTickPositionsArray {
    if (this.lessonRange[0] === undefined || this.lessonRange[1] === undefined) {
      return [] as AxisTickPositionsArray
    }
    const [min, max] = this.lessonRange
    return range(min, max + 2)
  }

  get lessonRange(): [number, number] {
    const sortedLessonNumbers = this.allLessonNumbers.sort((a, b) => a - b)
    const minCompletedLessonNumber = sortedLessonNumbers[0]
    const maxCompletedLessonNumber = sortedLessonNumbers[sortedLessonNumbers.length - 1]
    return [minCompletedLessonNumber ?? 0, maxCompletedLessonNumber ?? 0]
  }

  get studentIds() {
    return this.args.studentsReportData.map(({ id }) => id)
  }

  yAxisLabelFormatter(this: AxisLabelsFormatterContextObject) {
    return `${this.value}`
  }

  toolTipFormatter(this: Point) {
    const { lesson } = this as Point & { lesson: number }
    return lesson.toString()
  }

  <template>
    <div class="relative" ...attributes>
      <ChartTooltip @text={{t "reporting.tooltips.courseProgressHistory.chartInfo"}} />
      <div class="absolute inset-0">
        <SegmentedBar
          @categories={{this.studentIds}}
          @data={{this.seriesData}}
          @customLegend={{this.customLegend}}
          @plotLines={{this.plotLines}}
          @tickPositions={{this.tickPositions}}
          @title={{t "reporting.charts.courseProgress.lessonsCompleted" htmlSafe=true}}
          @toolTipFormatter={{this.toolTipFormatter}}
          @yAxisLabelFormatter={{this.yAxisLabelFormatter}}
          @yAxisOffset={{-5}}
          @yRange={{this.lessonRange}}
        />
      </div>
    </div>
  </template>
}
